import { useState, useRef, useEffect } from 'react';
import { css } from '@emotion/react';
import Tooltip from '@atlaskit/tooltip';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives';

import { CardStatus } from '@components/learning/LearningSidebar/constants';
import { useDeviceSize } from '@hooks/useDeviceSize';

export enum Appearance {
  COURSE = 'course',
  LESSON = 'lesson',
  SECTION = 'section',
}

interface TextTooltipProps {
  text: string;
  status?: CardStatus;
  appearance?: Appearance;
  isSelected: boolean;
}

/**
 * Props for the TextTooltip component
 * @param {string} text - Text to display in the tooltip
 * @param {CardStatus} status - Current status of the item (completed, or default)
 * @param {Appearance} appearance - Whether the item is a course card or lesson card
 * @param {boolean} isSelected - Whether the item is selected
 */

const MOBILE_SCREEN_WIDTH = 650;

export const TextTooltip: React.FC<TextTooltipProps> = ({ text, status, appearance, isSelected }) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const deviceSize = useDeviceSize();
  const isMobile = deviceSize <= MOBILE_SCREEN_WIDTH;

  useEffect(() => {
    const checkTruncation = (): void => {
      if (textRef.current) {
        // Check if the text is truncated by comparing scrollWidth to clientWidth
        setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);

    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, [text]);

  const isLesson = appearance === Appearance.LESSON;
  const isCourse = appearance === Appearance.COURSE;
  const isSection = appearance === Appearance.SECTION;

  const textElement = (
    <p
      data-testid="sidebar-text-tooltip"
      ref={textRef}
      css={[
        isCourse && courseTitleStyles,
        isCourse && isSelected && titleSelectedStyles,
        isLesson && lessonTitleStyles,
        isMobile && isLesson && regularDecorationStyles,
        isLesson && isSelected && titleSelectedStyles,
        sectionTitleStyles,
        isCourse && courseMaxWidth,
        isLesson && lessonMaxWidth,
        status === CardStatus.COMPLETED && sectionTitleCompletedStyles,
        status === CardStatus.DEFAULT && sectionTitleDefaultStyles,
        isSection && isSelected && sectionTitleSelectedStyles,
      ]}
    >
      {text}
    </p>
  );

  return isTruncated ? (
    <Tooltip content={text} position={isMobile ? 'bottom' : 'right'}>
      {textElement}
    </Tooltip>
  ) : (
    textElement
  );
};

const sectionTitleStyles = css({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  lineHeight: '24px',
  fontSize: '16px',
  marginTop: 0,
  marginLeft: '10px',
  maxWidth: '330px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',

  [media.below.xs]: {
    maxWidth: '290px',
  },
});

const sectionTitleSelectedStyles = css({
  fontWeight: 600,
  color: token('color.text.selected'),
});

const sectionTitleCompletedStyles = css({
  color: token('color.text.subtlest'),
});

const sectionTitleDefaultStyles = css({
  color: token('color.text.subtle'),
});

const lessonTitleStyles = css({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  lineHeight: '24px',
  fontSize: '16px',
  color: token('color.text.subtle'),
  marginTop: 0,
  marginRight: 'auto',
  marginLeft: token('space.025'),

  '&:hover': {
    textDecoration: 'underline',
  },
});

const regularDecorationStyles = css({
  '&:hover': {
    textDecoration: 'none',
  },
});

const titleSelectedStyles = css({
  fontWeight: '500 !important',
  color: token('color.text'),
});

const courseTitleStyles = css({
  color: token('color.text.subtlest'),
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  marginTop: 0,
});

const courseMaxWidth = css({
  marginLeft: 0,
  maxWidth: '320px',
});

const lessonMaxWidth = css({
  marginLeft: 0,
  marginRight: 'auto',
  maxWidth: '290px',

  [media.below.xs]: {
    maxWidth: '270px',
  },
});
